/**
 * AchievementSystem — Tracks and awards Spellroads achievements.
 *
 * Achievements are defined in /src/data/achievements.json.
 * Progress is saved/loaded via SaveSystem as part of the main snapshot.
 * Toast notifications are shown when an achievement is unlocked.
 *
 * EventBus listeners:
 *   combat:victory          → check combat achievements
 *   combat:victory_nodmg    → check flawless victory / untouched
 *   player:levelUp          → check level achievements
 *   world:regionVisited     → track exploration
 *   achievement:check       → manually check a specific achievement by id
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import achievementsData from '../data/achievements.json';

const AchievementSystem = {
  /** @type {object[]} All achievement definitions */
  _defs: achievementsData,

  /**
   * Progress map: achievementId → { count: number, unlocked: boolean, unlockedAt: number|null }
   * @type {Object<string, object>}
   */
  _progress: {},

  /** @type {Function[]} Unsubscribe functions for EventBus listeners */
  _unsubs: [],

  /** @type {Function|null} Optional callback when achievement unlocked */
  onUnlock: null,

  // ── Init ───────────────────────────────────────────────────────────────────

  init() {
    this._progress = {};
    for (const def of this._defs) {
      this._progress[def.id] = { count: 0, unlocked: false, unlockedAt: null };
    }

    // Remove any prior listeners
    for (const unsub of this._unsubs) unsub();
    this._unsubs = [];

    // ── EventBus wiring ──────────────────────────────────────────────────────

    // combat:victory — fired with { enemies, partyTookDamage, turnCount }
    this._unsubs.push(EventBus.on('combat:victory', (data = {}) => {
      this.trigger('combat_victory', data);
      if (!data.partyTookDamage) {
        this.trigger('combat_victory_nodmg', data);
      }
    }));

    // combat:enemyKilled — fired with { enemy: { id, type, isBoss } }
    this._unsubs.push(EventBus.on('combat:enemyKilled', (data = {}) => {
      this.trigger('enemy_killed', { enemyId: data.enemy?.id, amount: 1 });
      this.trigger('enemy_type_killed', { enemyType: data.enemy?.type, amount: 1 });
      if (data.enemy?.isBoss) {
        this.trigger('boss_killed', data);
      }
    }));

    // combat:bigDamage — fired with { damage, charId }
    this._unsubs.push(EventBus.on('combat:bigDamage', (data = {}) => {
      if ((data.damage ?? 0) >= 100) {
        this.trigger('damage_in_one_turn', data);
      }
    }));

    // combat:surviveAt1HP — fired when a character survives a hit at 1 HP
    this._unsubs.push(EventBus.on('combat:surviveAt1HP', (data = {}) => {
      this.trigger('survive_at_1hp', data);
    }));

    // combat:characterRevived — fired when a KO'd character is revived
    this._unsubs.push(EventBus.on('combat:characterRevived', (data = {}) => {
      this.trigger('character_revived', data);
    }));

    // player:levelUp — fired with { charId, level }
    this._unsubs.push(EventBus.on('player:levelUp', (data = {}) => {
      const level = data.level ?? 0;
      // Check level-based achievements: fire with the level as the running count
      // For "Legendary" (level 10) we check if level >= threshold
      const prog = this._progress['legendary'];
      if (prog && !prog.unlocked && level >= 10) {
        prog.count = level;
        prog.unlocked = true;
        prog.unlockedAt = Date.now();
        this._onUnlocked(this._defs.find(d => d.id === 'legendary'));
      }
    }));

    // world:regionVisited — fired with { regionId }
    this._unsubs.push(EventBus.on('world:regionVisited', (data = {}) => {
      this.trigger('region_explored', data);
    }));

    // world:biomeVisited — fired with { biome }
    this._unsubs.push(EventBus.on('world:biomeVisited', (data = {}) => {
      this.trigger('biome_visited', data);
    }));

    // world:tilesTraveled — fired with { amount }
    this._unsubs.push(EventBus.on('world:tilesTraveled', (data = {}) => {
      this.trigger('tiles_traveled', data);
    }));

    // world:seasonChanged — fired with { season }
    this._unsubs.push(EventBus.on('world:seasonChanged', (data = {}) => {
      this.trigger('season_changed', data);
    }));

    // dungeon:complete — fired when a dungeon is cleared
    this._unsubs.push(EventBus.on('dungeon:complete', (data = {}) => {
      this.trigger('dungeon_complete', data);
    }));

    // camp:rest — fired when party rests at camp
    this._unsubs.push(EventBus.on('camp:rest', (data = {}) => {
      this.trigger('camp_rest', data);
    }));

    // economy:goldSpent — fired with { amount }
    this._unsubs.push(EventBus.on('economy:goldSpent', (data = {}) => {
      this.trigger('gold_spent', data);
    }));

    // economy:goldAccumulated — fired with { total } when gold exceeds threshold
    this._unsubs.push(EventBus.on('economy:goldAccumulated', (data = {}) => {
      this.trigger('gold_accumulated', data);
    }));

    // fame:tierReached — fired with { tier, fame }
    this._unsubs.push(EventBus.on('fame:tierReached', (data = {}) => {
      this.trigger('fame_tier', { tier: data.tier });
      if (data.fame >= 500) this.trigger('fame_threshold', { amount: data.fame });
      else if (data.fame >= 100) this.trigger('fame_threshold', { amount: data.fame });
    }));

    // fame:updated — fired with { fame }
    this._unsubs.push(EventBus.on('fame:updated', (data = {}) => {
      const fame = data.fame ?? 0;
      if (fame >= 100) this.trigger('fame_threshold', { amount: fame });
    }));

    // skill:unlocked — fired with { charId, skillId }
    this._unsubs.push(EventBus.on('skill:unlocked', (data = {}) => {
      this.trigger('skill_unlocked', data);
    }));

    // equipment:fullArmorSet — fired when a character equips all armor slots
    this._unsubs.push(EventBus.on('equipment:fullArmorSet', (data = {}) => {
      this.trigger('full_armor_set', data);
    }));

    // party:maxLevel — fired when entire party is at max level
    this._unsubs.push(EventBus.on('party:maxLevel', (data = {}) => {
      this.trigger('full_party_max_level', data);
    }));

    // chargen:complete — fired when character creation finishes
    this._unsubs.push(EventBus.on('chargen:complete', (data = {}) => {
      this.trigger('chargen_complete', data);
    }));

    // debug:revealMap
    this._unsubs.push(EventBus.on('debug:revealMap', (data = {}) => {
      this.trigger('debug_revealmap', data);
    }));

    // achievement:check — manual check by id
    this._unsubs.push(EventBus.on('achievement:check', ({ id } = {}) => {
      if (id) this.check(id);
    }));

    DebugLog.info(`[achievement] AchievementSystem initialized — ${this._defs.length} achievements`);
  },

  // ── Trigger ────────────────────────────────────────────────────────────────

  /**
   * Notify the achievement system that an event occurred.
   * @param {string} triggerName — Event type (e.g. 'quest_complete', 'combat_victory')
   * @param {object} [ctx={}] — Context data (questId, enemyId, tier, etc.)
   */
  trigger(triggerName, ctx = {}) {
    for (const def of this._defs) {
      if (def.trigger !== triggerName) continue;

      const prog = this._progress[def.id];
      if (!prog || prog.unlocked) continue;

      let shouldCount = false;

      // Match by context specificity
      if (def.questId)    shouldCount = ctx.questId   === def.questId;
      else if (def.enemyId)    shouldCount = ctx.enemyId   === def.enemyId;
      else if (def.enemyType)  shouldCount = ctx.enemyType === def.enemyType;
      else if (def.tier)       shouldCount = ctx.tier      === def.tier;
      else if (def.vehicleId)  shouldCount = ctx.vehicleId === def.vehicleId;
      else                     shouldCount = true; // generic threshold

      if (!shouldCount) continue;

      prog.count += (ctx.amount ?? 1);

      const threshold = def.threshold ?? 1;
      if (prog.count >= threshold) {
        prog.unlocked   = true;
        prog.unlockedAt = Date.now();
        this._onUnlocked(def);
      }
    }
  },

  /**
   * Manually check a specific achievement by id.
   * Re-evaluates whether the achievement should be unlocked based on current state.
   * For achievements that rely on external state, callers must supply ctx.
   * @param {string} achievementId
   * @param {object} [ctx={}]
   */
  check(achievementId, ctx = {}) {
    const def = this._defs.find(d => d.id === achievementId);
    if (!def) {
      DebugLog.warn(`[achievement] check(): unknown id "${achievementId}"`);
      return;
    }
    this.trigger(def.trigger, ctx);
  },

  /**
   * Force-unlock an achievement by id (e.g. for debug or guaranteed events).
   * @param {string} achievementId
   * @returns {boolean} true if newly unlocked, false if already unlocked or unknown
   */
  unlock(achievementId) {
    const def = this._defs.find(d => d.id === achievementId);
    if (!def) {
      DebugLog.warn(`[achievement] unlock(): unknown id "${achievementId}"`);
      return false;
    }

    const prog = this._progress[def.id];
    if (!prog) return false;
    if (prog.unlocked) return false;

    prog.count      = def.threshold ?? 1;
    prog.unlocked   = true;
    prog.unlockedAt = Date.now();
    this._onUnlocked(def);

    EventBus.emit('achievement:unlocked', { id: def.id, name: def.name, def });
    return true;
  },

  _onUnlocked(def) {
    if (!def) return;
    DebugLog.event(`[achievement] Unlocked: ${def.id} — ${def.name}`);

    EventBus.emit('achievement:unlocked', { id: def.id, name: def.name, def });

    // Show toast notification
    try {
      import('../ui/Toast.js').then(m => {
        m.default.show(`${def.icon ?? '🏆'} Achievement unlocked: ${def.name}`, {
          type: 'success',
          duration: 3500,
        });
      });
    } catch (_) {}

    if (typeof this.onUnlock === 'function') {
      this.onUnlock(def);
    }
  },

  // ── Queries ────────────────────────────────────────────────────────────────

  /** Get all achievement definitions with current progress merged in. */
  getAll() {
    return this._defs.map(def => ({
      ...def,
      progress: this._progress[def.id] ?? { count: 0, unlocked: false, unlockedAt: null },
    }));
  },

  /** Get only unlocked achievements. */
  getUnlocked() {
    return this.getAll().filter(a => a.progress.unlocked);
  },

  /** Get achievements by category. */
  getByCategory(category) {
    return this.getAll().filter(a => a.category === category);
  },

  /**
   * Returns array of progress objects for the debug API and Playwright tests.
   * Format: { id, name, unlocked, progress, total }
   */
  getProgressList() {
    return this._defs.map(def => {
      const prog = this._progress[def.id] ?? { count: 0, unlocked: false, unlockedAt: null };
      return {
        id: def.id,
        name: def.name,
        category: def.category,
        unlocked: prog.unlocked,
        progress: prog.count,
        total: def.threshold ?? 1,
        unlockedAt: prog.unlockedAt,
      };
    });
  },

  /** Get progress object for serialization (SaveSystem). */
  getProgress() {
    return JSON.parse(JSON.stringify(this._progress));
  },

  /** Restore progress from SaveSystem. */
  restoreProgress(saved) {
    if (!saved || typeof saved !== 'object') return;
    for (const [id, prog] of Object.entries(saved)) {
      if (this._progress[id]) {
        this._progress[id] = prog;
      }
    }
    DebugLog.info(`[achievement] Progress restored — ${this.getUnlocked().length} unlocked`);
  },

  /** Unlock count summary for debug. */
  getSummary() {
    const total    = this._defs.length;
    const unlocked = this.getUnlocked().length;
    return { total, unlocked, percentage: Math.round(unlocked / total * 100) };
  },
};

export default AchievementSystem;
